// Leaderboard data service
// Talks to the server-side /api/leaderboard gatekeeper. No database key and no
// emails/password hashes ever reach the browser — the endpoint returns only
// entries plus a SAFE users map (displayName + avatar).

import { getSavedAuth } from './authService';

const API_URL = '/api/leaderboard';

// One page load asks for the board several times at once (table, Grand
// Masters, reign history). They share a single request for a few seconds;
// saving a score clears it so the new run shows straight away.
let shared = null;
const SHARE_MS = 3000;

export const fetchLeaderboardData = async () => {
  if (shared && Date.now() - shared.at < SHARE_MS) return shared.promise;
  const promise = requestLeaderboardData();
  shared = { at: Date.now(), promise };
  promise.catch(() => { if (shared && shared.promise === promise) shared = null; });
  return promise;
};

// Fetch leaderboard data (entries + safe users) from the server
const requestLeaderboardData = async () => {
  try {
    const response = await fetch(API_URL, { method: 'GET' });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const record = await response.json();

    // Ensure both users and entries exist in the structure
    if (!record.users) record.users = {};
    if (!record.entries) record.entries = [];

    return record;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    // Re-throw so callers abort instead of acting on empty data.
    throw error;
  }
};

// Save a new leaderboard entry (server appends it; it can never shrink the board)
export const saveLeaderboardEntry = async (entry) => {
  try {
    // The server only takes a run under a name with that name's password,
    // which this device keeps from signing in. It is never stored with the run.
    const auth = getSavedAuth();
    const owned = auth && String(auth.username).toLowerCase() === String(entry.username).toLowerCase();
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(owned ? { ...entry, password: auth.password } : entry),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const result = await response.json();
    shared = null;
    if (!result.success) {
      return { success: false, error: result.error || 'Failed to save entry' };
    }
    console.log('Leaderboard entry saved:', result.entry);
    return { success: true, entry: result.entry };
  } catch (error) {
    console.error('Error saving leaderboard entry:', error);
    return { success: false, error: error.message };
  }
};

// A save that the site wrote twice is not a play. Until 2026-09-17 a new
// player's first score was saved a second time when registering logged them
// in, so the board holds pairs one second apart with the same score (the copy
// carries a slightly longer time). Nothing is deleted: the copy is simply not
// shown or counted.
const DOUBLE_SAVE_MS = 5000;

export const dropDoubleSaves = (entries) => {
  const seen = new Map();
  return entries.filter((entry) => {
    const key = `${(entry.username || '').toLowerCase()}|${entry.gameType}|${entry.score}`;
    const at = new Date(entry.createdAt || entry.date).getTime();
    const previous = seen.get(key);
    if (previous !== undefined && Math.abs(at - previous) <= DOUBLE_SAVE_MS) return false;
    seen.set(key, at);
    return true;
  });
};

// Get entries for a specific game type
export const getEntriesByGameType = async (gameType, limit = 50) => {
  const data = await fetchLeaderboardData();

  // Copy: the board data is shared between callers, so never sort it in place.
  let entries = dropDoubleSaves(data.entries || []);

  // Filter by game type if specified
  if (gameType && gameType !== 'all') {
    entries = entries.filter(e => e.gameType === gameType);
  }

  // Sort by score (desc), then by time (asc) for ties
  entries.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    return a.totalTime - b.totalTime;
  });

  return entries.slice(0, limit);
};

// One row per player: their best run in a game, plus every run they posted
// there (best first), so a player who plays a lot shows up once with the full
// history behind a tap instead of filling the table.
export const getPlayerBoard = async (gameType, limit = 50) => {
  const entries = await getEntriesByGameType(gameType, Infinity);
  const players = new Map();
  entries.forEach((entry) => {
    const key = (entry.username || '').toLowerCase();
    if (!players.has(key)) players.set(key, { ...entry, runs: [] });
    players.get(key).runs.push(entry);
  });
  return [...players.values()].slice(0, limit);
};

// Where a score would land on a game board, worked out before it is saved, so
// a player who just finished can see it whether or not they have an account.
export const getScoreStanding = async (gameType, score, totalTime) => {
  const players = await getPlayerBoard(gameType, Infinity);
  const better = players.filter(
    (p) => p.score > score || (p.score === score && p.totalTime <= totalTime),
  ).length;
  const tenth = players[9];
  return {
    rank: better + 1,
    players: players.length + 1,
    topTen: tenth ? { score: tenth.score, totalTime: tenth.totalTime } : null,
    best: players[0] ? { username: players[0].username, score: players[0].score, totalTime: players[0].totalTime } : null,
  };
};

// Get Grand Masters - players with 11+ best in ALL 4 games
export const getGrandMasters = async () => {
  const data = await fetchLeaderboardData();
  return computeGrandMasters(data.entries || []);
};

// The Grand Masters ranking for any list of entries. Pure, so the reign
// history can replay it one play at a time.
export const computeGrandMasters = (entries) => {

  // Group entries by username and find best score per game type
  const playerBestScores = {};

  entries.forEach(entry => {
    if (!playerBestScores[entry.username]) {
      playerBestScores[entry.username] = {
        avatar: entry.avatar,
        avatarType: entry.avatarType,
      };
    }

    const current = playerBestScores[entry.username][entry.gameType];

    // Keep the best score (highest score, or same score with faster time)
    if (!current ||
        entry.score > current.score ||
        (entry.score === current.score && entry.totalTime < current.totalTime)) {
      playerBestScores[entry.username][entry.gameType] = {
        score: entry.score,
        totalTime: entry.totalTime,
        date: entry.date,
      };
    }
  });

  // Filter to only players with 11+ in ALL 4 game types
  const grandMasters = [];
  const gameTypes = ['character', 'image', 'devilfruit', 'bounty'];

  Object.entries(playerBestScores).forEach(([username, scores]) => {
    // Legend rule: 11+ best in ALL 4 games (Bounty Battle is scored out of 11).
    const hasAllGames = gameTypes.every(type =>
      scores[type] && scores[type].score >= 11
    );

    if (hasAllGames) {
      const characterScore = scores['character'];
      const imageScore = scores['image'];
      const devilFruitScore = scores['devilfruit'];
      const bountyScore = scores['bounty'];

      const totalScore = characterScore.score + imageScore.score + devilFruitScore.score + bountyScore.score;
      const totalTime = characterScore.totalTime + imageScore.totalTime + devilFruitScore.totalTime + bountyScore.totalTime;

      // Get the most recent date among all 4 games
      const latestDate = [characterScore.date, imageScore.date, devilFruitScore.date, bountyScore.date]
        .sort((a, b) => new Date(b) - new Date(a))[0];

      // One title, not three. Clearing 11+ in all four games is the whole
      // achievement; splitting the people who manage it into Grand Master /
      // Yonko / Pirate King only made the rarest thing on the site look like a
      // consolation prize for anyone under 54. Everyone who gets through the
      // gate above is a Pirate King. (Max possible total is 15+15+15+11 = 56.)
      const tier = 'PIRATE KING';
      const tierIcon = '🏴‍☠️';

      grandMasters.push({
        username,
        avatar: scores.avatar,
        avatarType: scores.avatarType,
        totalScore,
        totalTime,
        characterScore: characterScore.score,
        imageScore: imageScore.score,
        devilFruitScore: devilFruitScore.score,
        bountyScore: bountyScore.score,
        date: latestDate,
        tier,
        tierIcon,
      });
    }
  });

  // Sort by total score (desc), then total time (asc)
  grandMasters.sort((a, b) => {
    if (b.totalScore !== a.totalScore) return b.totalScore - a.totalScore;
    return a.totalTime - b.totalTime;
  });

  return grandMasters;
};

// Sort order used by every game table: highest score, then fastest time.
// Array.sort is stable, so on a full tie the earlier post stays ahead.
const byScoreThenTime = (a, b) => (b.score !== a.score ? b.score - a.score : a.totalTime - b.totalTime);

// Who has held #1, and for how long, worked out from the plays themselves.
// Nothing is stored: every page load replays the board in the order the
// server saved it, so the numbers are always current and a restored backup
// brings them back exactly.
//   current:  today's #1, since when, and how many runs by other players
//             came in while they held it (challengers)
//   longest:  the reign that held off the most challengers (days break ties),
//             so a quiet month early on cannot beat a busy week
//   first:    the first player ever to hold #1 on this board
export const computeReigns = (entries, board, now = Date.now()) => {
  const GAMES = ['character', 'image', 'devilfruit', 'bounty'];
  const isHall = board === 'grand-masters';
  const plays = dropDoubleSaves(entries);
  const relevant = isHall ? plays.filter((e) => GAMES.includes(e.gameType)) : plays.filter((e) => e.gameType === board);
  const reigns = [];
  let leader = null;
  let hallSoFar = [];
  let bestSoFar = null;
  let hallAvatar = null;

  relevant.forEach((entry) => {
    let next = leader && leader.username;
    if (isHall) {
      hallSoFar.push(entry);
      // A run under 11 cannot change who qualifies or how they rank.
      if (entry.score >= 11 || !leader) {
        const top = computeGrandMasters(hallSoFar)[0];
        next = top ? top.username : null;
        if (top && leader && top.username === leader.username) leader.avatar = top.avatar;
        if (top) hallAvatar = top.avatar;
      }
    } else if (!bestSoFar || byScoreThenTime(entry, bestSoFar) < 0) {
      bestSoFar = entry;
      next = entry.username;
    }

    const at = new Date(entry.createdAt || entry.date).getTime();
    if (next && (!leader || next !== leader.username)) {
      if (leader) leader.until = at;
      leader = { username: next, avatar: isHall ? hallAvatar : entry.avatar, since: at, until: null, challengers: 0 };
      reigns.push(leader);
    } else if (leader && entry.username.toLowerCase() !== leader.username.toLowerCase()) {
      leader.challengers += 1;
    }
  });

  if (!leader) return null;
  const days = (r) => ((r.until || now) - r.since) / 86400000;
  const longest = reigns.reduce((best, r) =>
    (r.challengers > best.challengers || (r.challengers === best.challengers && days(r) > days(best)) ? r : best));
  const summary = (r) => ({ username: r.username, avatar: r.avatar, since: r.since, days: Math.floor(days(r)), challengers: r.challengers, current: !r.until });
  return {
    current: summary(leader),
    longest: summary(longest),
    first: reigns[0].username,
    changes: reigns.length,
  };
};

